import { useContext, useEffect, useRef, useState } from "react"
import { useShallow } from "zustand/react/shallow"
import { useNavigate, useParams } from "react-router-dom"
import moment from "moment"

import { IFilterApplications, IApplicationsPage, IApplicationStatuses, IAccessSites } from "./types"
import { ApplicationsPage } from "./ui"
import { ApplicationsFilter } from "./ui.filter"
import { loadApplicationViewMode, saveApplicationViewMode, TApplicationsViewMode } from './view-mode-storage'

import { useValue } from "@hooks/useValue"
import { useDelayedInput } from "@hooks/useDelayedInput"

import { useDict } from "@api/dict/state"
import { useLogin } from "@api/login/state"
import { useContracts } from "@api/contracts/state"
import { useApplications } from "@api/applications/state"
import { getApplicationTableFilterOptions, trackApplicationViewModeUsage } from '@api/applications/api'
import { IApplicationsFilter, IApplicationsList } from "@api/applications/state.types"

import { IModalConfirm, ModalConfirm } from "@primitive/modal.confirm"

import { CardModeType } from "@smart/common.type"
import { IModalApplicationMailingsContainer, ModalApplicationMailingsContainer } from "@smart/modalApplicationMailings"
import { CardApplicationContainer, ICardApplicationContainer } from "@components/smart/cardApplication"
import { ModalApplicationsPromoActions, IModalApplicationsPromoActions } from "@smart/modalApplicationsPromo"

import { NotificationContext, notifyOpen } from "@ui/notification/notification.provider"
import realtimeService from "@services/realtime.service"


const DELAYED_ACTION_VIEWED_STORAGE_KEY = 'crm_applications_viewed_delayed_actions'

const createDefaultApplicationsFilter = (): IApplicationsFilter => ({
    applicationStatuses: '', accessSites: '', dateNextCallFrom: '', dateNextCallBefore: '',
    dateStartEducationFrom: '', dateStartEducationBefore: '', dateCreateFrom: '', dateCreateBefore: '',
    isBlackListOnly: '', isBlackListHide: '', isDailyApplications: '1', isOvernightApplications: '1',
})

const getDelayedActionViewKey = (item: IApplicationsList) => `${item.applicationID}|${item.dateNextCall || ''}|${item.timeNextCall || ''}`

const getStoredDelayedActionViewKeys = () => {
    try {
        const storedValue = localStorage.getItem(DELAYED_ACTION_VIEWED_STORAGE_KEY)
        const parsedValue = storedValue ? JSON.parse(storedValue) : []

        return Array.isArray(parsedValue) ? parsedValue.filter((item) => typeof item === 'string') : []
    } catch {
        return []
    }
}

const setStoredDelayedActionViewKeys = (keys: string[]) => {
    try {
        localStorage.setItem(DELAYED_ACTION_VIEWED_STORAGE_KEY, JSON.stringify(keys))
    } catch {}
}


/** Контейнер страница - Заявки */
export const ApplicationsContainer = () => {
    const navigate = useNavigate()
    const { applicationID } = useParams()
    const notify = useContext(NotificationContext)
    const { loginAccessSites, loginName } = useLogin(useShallow((state) => ({
        loginAccessSites: state.profile.accessSites,
        loginName: state.profile.loginName,
    })))

    const filterIsOpen = useValue<boolean>(false)
    const filterIsActivate = useValue<boolean>(false)
    const filterApplicationStatuses = useValue<IApplicationStatuses[]>([])
    const filterSites = useValue<IAccessSites[]>([])
    const filterDateNextCallFrom = useValue<string>('')
    const filterDateNextCallBefore = useValue<string>('')
    const filterDateStartEducationFrom = useValue<string>('')
    const filterDateStartEducationBefore = useValue<string>('')
    const filterDateCreateFrom = useValue<string>('')
    const filterDateCreateBefore = useValue<string>('')
    const filterIsBlackListOnly = useValue<string>('')
    const filterIsBlackListHide = useValue<string>('')
    const filterIsDailyApplications = useValue<string>('1')
    const filterIsOvernightApplications = useValue<string>('1')
    const [viewedDelayedActionKeys, setViewedDelayedActionKeys] = useState<string[]>(getStoredDelayedActionViewKeys)
    const [viewMode, setViewMode] = useState<TApplicationsViewMode>(() => loadApplicationViewMode(loginName))
    const [columnFilters, setColumnFilters] = useState<IApplicationsPage['columnFilters']>({})
    const viewModeRef = useRef<TApplicationsViewMode>(viewMode)
    const viewModeLoginRef = useRef(loginName)
    const trackedViewModeUsageRef = useRef('')
    const appliedColumnFiltersRef = useRef<IApplicationsPage['columnFilters']>({})
    const pageNumberRef = useRef(1)

    /** Таймер, для автоматического обновления основного списка */
    const intervalRef = useRef<NodeJS.Timeout | null>(null)
    const isApplicationStatusesLoadedRef = useRef(false)
    const contractCreationsInProgressRef = useRef<Set<number>>(new Set())

    /** Метка о том, что доступ к данному разделу запрещен */
    const isAccessDenied = useValue<boolean>(false)

    /** Пагинация - Изменение номера страницы */
    const paginationPageNumber = useValue<number>(1)

    /** Окно подтверждения */
    const modalConfirm = useValue<IModalConfirm>({isOpen: false})
    /** Окно с рассылками */
    const modalMailings = useValue<IModalApplicationMailingsContainer>({isOpen: false})
    /** Окно со списком промо-заявок */
    const modalApplicationsPromo = useValue<IModalApplicationsPromoActions>({isOpen: false})

    /** Окно с анкетой заявки */
    const cardApplications = useValue<ICardApplicationContainer>({isOpen: false})

    
    // Хранилище страницы - Заявки
    const { 
        isLoadingPage, applicationsList, applicationsTableColumns, applicationsPageSize, applicationsStatistics,
        applicationsTotalCount, applicationsNew, applicationsDelayedCall, applicationsExecutionContract,

        actionGetApplications, actionSetApplicationBlackList, clearStateApplications,
    } = useApplications(useShallow((state) => ({
        isLoadingPage: state.applications.isLoading,

        applicationsList: state.applications.items,
        applicationsTableColumns: state.applications.tableColumns,
        applicationsPageSize: state.applications.pageSize,
        applicationsTotalCount: state.applications.totalCount,
        applicationsNew: state.applications.new,
        applicationsDelayedCall: state.applications.delayedCall,
        applicationsExecutionContract: state.applications.executionContract,

        applicationsStatistics: state.applicationsStatistics,

        actionGetApplications: state.actionGetApplications,
        actionSetApplicationBlackList: state.actionSetApplicationBlackList,
        clearStateApplications: state.clearStateApplications,
    })))

    // Хранилище страницы - Договора
    const { actionSetContractCardSave } = useContracts(useShallow((state) => ({
        actionSetContractCardSave: state.actionSetContractCardSave
    })))

    // Хранилище справочники
    const dictState = useDict(useShallow((state) => state))

    const loadApplicationStatuses = () => {
        if (isApplicationStatusesLoadedRef.current) return

        isApplicationStatusesLoadedRef.current = true

        dictState.actionGetApplicationStatuses().then((res) => {
            filterApplicationStatuses.setValue(
                res.map((item) => ({
                    id: item.applicationStatusID,
                    name: item.applicationStatusName,
                    isSelected: false
                }))
            )
        })
    }
    
    const trackLoadedViewMode = (mode: TApplicationsViewMode) => {
        const usageKey = `${loginName}\u0000${mode}`
        if (!loginName || trackedViewModeUsageRef.current === usageKey) return

        trackedViewModeUsageRef.current = usageKey
        void trackApplicationViewModeUsage(mode).then((response) => {
            if (response.status !== 200 && trackedViewModeUsageRef.current === usageKey) trackedViewModeUsageRef.current = ''
        }).catch(() => {
            if (trackedViewModeUsageRef.current === usageKey) trackedViewModeUsageRef.current = ''
        })
    }

    /** Загрузка первичных данных */
    const loadPrimaryData = (search: string, isShowLoading: boolean = true) => {
        if (!loginName) return

        const requestedViewMode = viewModeRef.current

        actionGetApplications(pageNumberRef.current, search, {
            ...filtersRef.current,
            viewMode: requestedViewMode,
            columnFilters: requestedViewMode === 'table' ? JSON.stringify(appliedColumnFiltersRef.current) : undefined,
        }, isShowLoading).then((res) => {
            if (res.status === 200) {
                isAccessDenied.setValue(false)
                loadApplicationStatuses()
                trackLoadedViewMode(requestedViewMode)
            }

            if (res.status !== 200 && res.status !== 500) {
                if (res.status === 403) {
                    clearStateApplications()
                    isAccessDenied.setValue(true)
                } else {
                    notify && notifyOpen(res.message, 'error', 2000, notify)
                }
            }
        })
    }

    /** Запуск загрузки данных через полсекунды */
    const { value: txtSearch, setValue: setTxtSearch } = useDelayedInput<string>("", 500, (val) => reloadFromFirstPage(val))

    const searchRef = useRef(txtSearch)

    const filtersRef = useRef<IApplicationsFilter>(createDefaultApplicationsFilter())

    const reloadFromFirstPage = (search = searchRef.current) => {
        if (pageNumberRef.current === 1) loadPrimaryData(search)
        else paginationPageNumber.setValue(1)
    }

    const handleViewModeChange = (mode: 'list' | 'table') => {
        saveApplicationViewMode(loginName, mode)
        if (viewModeRef.current === mode) return
        viewModeRef.current = mode
        setViewMode(mode)
        appliedColumnFiltersRef.current = {}
        setColumnFilters({})
        reloadFromFirstPage()
    }

    const handleClearColumnFilters = () => {
        appliedColumnFiltersRef.current = {}
        setColumnFilters({})
        reloadFromFirstPage()
    }

    const handleColumnFilterChange = (name: string, values: string[]) => {
        if (viewModeRef.current !== 'table') return
        const next = { ...appliedColumnFiltersRef.current }
        const normalizedValues = Array.from(new Set(values.map(value => value.trim()).filter(Boolean)))
        if (normalizedValues.length) next[name] = normalizedValues
        else delete next[name]
        if (JSON.stringify(next) === JSON.stringify(appliedColumnFiltersRef.current)) return
        appliedColumnFiltersRef.current = next
        setColumnFilters(next)
        reloadFromFirstPage()
    }

    const loadColumnFilterOptions = async (name: string) => {
        const response = await getApplicationTableFilterOptions(name)
        if (response.status !== 200) throw new Error(response.message || 'Не удалось загрузить значения фильтра.')
        return response.data?.values || []
    }

    // ================================================================================

    useEffect(() => {
        if (viewModeLoginRef.current === loginName) return

        const storedViewMode = loadApplicationViewMode(loginName)
        viewModeLoginRef.current = loginName
        viewModeRef.current = storedViewMode
        setViewMode(storedViewMode)
        appliedColumnFiltersRef.current = {}
        setColumnFilters({})
        reloadFromFirstPage()
    }, [loginName]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (applicationID) {
            handleOpenCardApplication(true, "edit", Number(applicationID))
        }
    }, [applicationID]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        return (() => {
            isApplicationStatusesLoadedRef.current = false
            clearStateApplications()

            setTxtSearch('')
            paginationPageNumber.setValue(1)

            filterIsOpen.setValue(false)
            filterIsActivate.setValue(false)
            filterApplicationStatuses.setValue([])
            filterSites.setValue([])
            filterDateNextCallFrom.setValue('')
            filterDateNextCallBefore.setValue('')
            filterDateStartEducationFrom.setValue('')
            filterDateStartEducationBefore.setValue('')
            filterDateCreateFrom.setValue('')
            filterDateCreateBefore.setValue('')
            filterIsBlackListOnly.setValue('')
            filterIsBlackListHide.setValue('')
            filterIsDailyApplications.setValue('1')
            filterIsOvernightApplications.setValue('1')
        })
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        searchRef.current = txtSearch;
    }, [txtSearch])

    useEffect(() => {
        let applicationStatusesListSelected = ''

        filterApplicationStatuses.value.forEach((item) => {
            if (item.isSelected) {
                applicationStatusesListSelected += `'${item.name}',`
            }
        })

        let accessSitesListSelected = ''

        filterSites.value.forEach((item) => {
            if (item.isSelected) {
                accessSitesListSelected += `'${item.name}',`
            }
        })

        filtersRef.current = {
            applicationStatuses: applicationStatusesListSelected,
            accessSites: accessSitesListSelected,
            dateNextCallFrom: filterDateNextCallFrom.value,
            dateNextCallBefore: filterDateNextCallBefore.value,
            dateStartEducationFrom: filterDateStartEducationFrom.value,
            dateStartEducationBefore: filterDateStartEducationBefore.value,
            dateCreateFrom: filterDateCreateFrom.value,
            dateCreateBefore: filterDateCreateBefore.value,
            isBlackListOnly: filterIsBlackListOnly.value,
            isBlackListHide: filterIsBlackListHide.value,
            isDailyApplications: filterIsDailyApplications.value,
            isOvernightApplications: filterIsOvernightApplications.value
        }
    }, [
        filterApplicationStatuses.value,
        filterSites,
        filterDateNextCallFrom.value,
        filterDateNextCallBefore.value,
        filterDateStartEducationFrom.value,
        filterDateStartEducationBefore.value,
        filterDateCreateFrom.value,
        filterDateCreateBefore.value,
        filterIsBlackListOnly.value,
        filterIsBlackListHide.value,
        filterIsDailyApplications.value,
        filterIsOvernightApplications.value
    ])

    useEffect(() => {
        pageNumberRef.current = paginationPageNumber.value
        loadPrimaryData(txtSearch)
    }, [paginationPageNumber.value]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const unsubscribeApplications = realtimeService.subscribe('applications', () => {
            loadPrimaryData(searchRef.current, false)
        })

        intervalRef.current = setInterval(() => {
            loadPrimaryData(searchRef.current, false)
        }, 60000)

        return () => {
            unsubscribeApplications()
            clearInterval(intervalRef.current!)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (loginAccessSites && loginAccessSites.length > 0) {
            filterSites.setValue(
                loginAccessSites.map((item) => ({
                    id: item.siteDbNameID,
                    name: item.siteDbName,
                    description: item.siteDbNameDescription,
                    isSelected: true
                }))
            )
        }
    }, [loginAccessSites]) // eslint-disable-line react-hooks/exhaustive-deps

    // ================================================================================

    /** Открытие/закрытие окна подтверждения */
    const handleOpenModalConfirm = (isOpen: boolean, headerText: string, contentText: string, applicationID: number, dataBaseName: string, actionType: 'createContract' | 'toBlackList', currentBlackList: number) => {
        modalConfirm.setValue({
            isOpen, headerText, contentText, 

            handleButtonCancel() {
                modalConfirm.setValue({isOpen: false})
            }, 
            
            async handleButtonOk() {
                switch (actionType) {
                    case "createContract": { // Создание нового договора
                        if (contractCreationsInProgressRef.current.has(applicationID)) break

                        contractCreationsInProgressRef.current.add(applicationID)
                        modalConfirm.setValue({isLoading: true})

                        try {
                            const res = await actionSetContractCardSave(0, {
                                actionForm: 'new', contractTypeID: 1, contractViewID: 1, contractStatusID: 1,
                                contractDate: (moment(new Date())).format('DD.MM.YYYY'), applicationID: Number(applicationID), dataBaseName: dataBaseName,
                                companyID: 0, companyContactFIO: '', companyContactPhone: '', companyContactEmail: '',
                                deliveryToWhom: '', deliveryAddress: '', comment: '', isPostPayment: 0, isLearningOutsideMoodle: 0
                            })

                            if (res.status === 200) {
                                modalConfirm.setValue({isOpen: false})
                                notify && notifyOpen('Данные успешно сохранены.', 'success', 1500, notify)

                                navigate('/lk/contracts')
                            }
                        } finally {
                            contractCreationsInProgressRef.current.delete(applicationID)
                            modalConfirm.setValue({isLoading: false})
                        }
                        break
                    }

                    case 'toBlackList': { // Отправка заявки в черный лист
                        actionSetApplicationBlackList(applicationID, `${currentBlackList === 0 ? 'add' : 'delete'}`).then((res) => {
                            if (res.status === 200) {
                                loadPrimaryData(txtSearch)
                                modalConfirm.setValue({isOpen: false})
                                notify && notifyOpen(`${currentBlackList === 0 ? 'Заявка успешно отправлена в черный лист' : 'Заявка успешно удалена из черного листа'}`, 'success', 1500, notify)
                            }
                        })
                        break
                    }
                }
            }
        })
    }

    /** Открытие/закрытие окна выбора рассылок в telegram */
    const handleOpenModalMailings = (isOpen: boolean, applicationID: number) => {
        modalMailings.setValue({
            isOpen, applicationID,
            handleButtonCancel() { modalMailings.setValue({ isOpen: false }) },
        })
    }

    const markDelayedActionViewed = (delayedActionViewKey?: string) => {
        if (!delayedActionViewKey) return

        setViewedDelayedActionKeys((prev) => {
            if (prev.includes(delayedActionViewKey)) return prev

            const updatedKeys = [...prev, delayedActionViewKey].slice(-500)
            setStoredDelayedActionViewKeys(updatedKeys)

            return updatedKeys
        })
    }

    const getCurrentDelayedActionViewKey = (cardID?: number) => {
        const currentApplication = applicationsList.find((item) => item.applicationID === cardID)

        return currentApplication?.isDelayedActionExpired === 1 ? getDelayedActionViewKey(currentApplication) : undefined
    }

    
    /** Открытие/закрытие анкеты заявки */
    const closeCardApplication = () => {
        cardApplications.setValue({isOpen: false})
        if (applicationID) navigate('/lk/applications', { replace: true })
    }

    const handleOpenCardApplication = (isOpen: boolean, cardMode: CardModeType, cardID?: number, delayedActionViewKey?: string) => {
        if (isOpen && cardID) markDelayedActionViewed(delayedActionViewKey || getCurrentDelayedActionViewKey(cardID))

        cardApplications.setValue({
            isOpen, cardMode, cardID,
            handleButtonCancel() { closeCardApplication() }, 
            handleButtonOk() {
                closeCardApplication()
                loadPrimaryData(txtSearch)
            }
        })
    }

    /** Открытие/закрытие списка заявок промо */
    const handleOpenCardApplicationPromo = (isOpen: boolean) => {
        modalApplicationsPromo.setValue({
            isOpen, 
            handleButtonCancel() {modalApplicationsPromo.setValue({isOpen: false})}, 
            handleButtonOk() {
                modalApplicationsPromo.setValue({isOpen: false})
                loadPrimaryData(txtSearch)
            }
        })
    }

    /** Кнопка применить (очистить) в окне - Фильтр по заявкам */
    const handleApplyClearFilter = (filterType: 'apply' | 'clear') => {
        switch (filterType) {
            case 'apply': {
                // Фильтр активен только если значения отличаются от начальных
                const isFilterActive = 
                    filterApplicationStatuses.value.some(s => s.isSelected) ||
                    filterSites.value.some(s => !s.isSelected) ||
                    filterDateNextCallFrom.value !== '' ||
                    filterDateNextCallBefore.value !== '' ||
                    filterDateStartEducationFrom.value !== '' ||
                    filterDateStartEducationBefore.value !== '' ||
                    filterDateCreateFrom.value !== '' ||
                    filterDateCreateBefore.value !== '' ||
                    filterIsBlackListOnly.value !== '' ||
                    filterIsBlackListHide.value !== '' ||
                    filterIsDailyApplications.value !== '1' ||
                    filterIsOvernightApplications.value !== '1'
                filterIsActivate.setValue(isFilterActive)
                reloadFromFirstPage()
                break
            }

            case 'clear': {
                filterIsActivate.setValue(false)
                filterApplicationStatuses.setValue(filterApplicationStatuses.value.map(status => ({...status, isSelected: false})))
                filterSites.setValue(filterSites.value.map(status => ({...status, isSelected: true})))
                filterDateNextCallFrom.setValue('')
                filterDateNextCallBefore.setValue('')
                filterDateStartEducationFrom.setValue('')
                filterDateStartEducationBefore.setValue('')
                filterDateCreateFrom.setValue('')
                filterDateCreateBefore.setValue('')
                filterIsBlackListOnly.setValue('')
                filterIsBlackListHide.setValue('')
                filterIsDailyApplications.setValue('1')
                filterIsOvernightApplications.setValue('1')

                filtersRef.current = createDefaultApplicationsFilter()
                appliedColumnFiltersRef.current = {}
                setColumnFilters({})
                reloadFromFirstPage()
                break
            }
        }
    }

    // ================================================================================

    /** Свойства передаваемые в компоненту */
    const propsToComponent: IApplicationsPage = {
        isLoadingPage,
        isAccessDenied,
        applicationsList,
        applicationsTableColumns,
        loginName,
        viewMode,
        columnFilters,
        onViewModeChange: handleViewModeChange,
        onColumnFilterChange: handleColumnFilterChange,
        onClearColumnFilters: handleClearColumnFilters,
        loadColumnFilterOptions,
        applicationsPageSize,
        paginationPageNumber,

        applicationsTotalCount, 
        applicationsNew, 
        applicationsDelayedCall, 
        applicationsExecutionContract,
        viewedDelayedActionKeys,
        
        applicationsStatistics,

        filterIsOpen, filterIsActivate, 
        txtSearch, setTxtSearch, 
        
        loadPrimaryData,
        handleOpenModalConfirm,
        handleOpenModalMailings,
        handleOpenCardApplication,
        handleOpenCardApplicationPromo,
        handleApplyClearFilter,
    }

    /** Свойства передаваемые в окно фильтра */
    const propsToFilter: IFilterApplications = {
        isOpen: filterIsOpen.value, 

        filterSites,

        filterApplicationStatuses: filterApplicationStatuses, 
        filterDateNextCallFrom: filterDateNextCallFrom, 
        filterDateNextCallBefore: filterDateNextCallBefore,
        filterDateStartEducationFrom: filterDateStartEducationFrom, 
        filterDateStartEducationBefore: filterDateStartEducationBefore,
        filterDateCreateFrom: filterDateCreateFrom, 
        filterDateCreateBefore: filterDateCreateBefore,
        filterIsBlackListOnly: filterIsBlackListOnly,
        filterIsBlackListHide: filterIsBlackListHide,
        filterIsDailyApplications: filterIsDailyApplications,
        filterIsOvernightApplications: filterIsOvernightApplications,

        handleApplyClearFilter,
        handleButtonCancel: () => filterIsOpen.setValue(false),
    }

    return <>
        <ApplicationsPage {...propsToComponent} />
        <ApplicationsFilter {...propsToFilter} />
        
        <ModalConfirm {...modalConfirm.value} />
        <ModalApplicationMailingsContainer {...modalMailings.value} />

        <CardApplicationContainer {...cardApplications.value} />
        <ModalApplicationsPromoActions {...modalApplicationsPromo.value} />
    </> 
}